import {
    AiIcon,
    Button,
    CountryFlag,
    Icon,
    Logo,
    LogoDark,
    ModalContent,
    modalContext,
    platformContext,
    TabMenu,
    themeContext
} from "shared";
import { authContext, usePermission } from "entities/user";
import { RoleMatrix } from "features/role_matrix";
import { routes } from "app";
import React, { FC, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import { MenuType } from "@yandex-int/hr-components/PageHeader/PageHeader.types";
import { ItemSimple } from "@yandex-int/hr-components/Menu/Menu";
import { HostsContextProvider } from "@yandex-int/hr-components/Context/HostsContext";
import { PageHeader as HrHeader } from "@yandex-int/hr-components/PageHeader/PageHeader";
import { Hosts, Platform } from "@yandex-int/hr-components/lib";
import { PopupAction } from "../popup-action";
import { usePageLayoutTranslations } from "../../lib/i18n";
import styles from "./page-header.module.scss";

const hosts: Hosts = {
    staff: {
        host: "staff.yandex-team.ru",
    },
    center: {
        host: "center.yandex-team.ru",
    },
};

interface IHeaderProps {
    className?: string;
    user?: any;
}

const dayIcon = (className: string) => (
    <Icon className={className} glyph="day" size="m"/>
);
const dayCheckedIcon = (className: string) => (
    <Icon className={className} glyph="dayChecked" size="m"/>
);

const RuIcon = () => {
    const t = usePageLayoutTranslations();

    return <CountryFlag
        countryCode="ru"
        className={styles.countryIcon}
        title={t("changeLanguage")}
    />;
};

const EnIcon = () => {
    const t = usePageLayoutTranslations();

    return <CountryFlag
        countryCode="gb"
        className={styles.countryIcon}
        title={t("changeLanguage")}
    />;
};

const hrIcon = (className: string) => (
    <Icon className={className} glyph="hr" size="m"/>
);


export const PageHeader: FC<IHeaderProps> = React.memo((props) => {
    const { i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const userInfo = useContext(authContext);
    const { className } = props;
    const [theme, setTheme] = useContext(themeContext);
    const [language, setLanguage] = useState("ru");
    const t = usePageLayoutTranslations();
    const { close, openModal } = useContext(modalContext);
    const platform = useContext(platformContext);
    const canSeeRoleMatrix = usePermission("canSeeRoleMatrix");
    const isIframe = useMemo(() => {
        const path = location.pathname.split("/");
        return path[1] === "iframe";
    }, [location.pathname]);

    useEffect(() => {
        const language = localStorage.getItem("language");
        if (language && ["ru", "en"].includes(language)) {
            setLanguage(language);
        }
    }, []);

    useEffect(() => {
        i18n.changeLanguage(language);
    }, [language]);

    const onToggleTheme = () => {
        const newTheme = theme === "light" ? "dark" : "light";
        setTheme(newTheme);
        localStorage.setItem("hrComponentsTheme", newTheme);
    };

    const onLanguageChanged = () => {
        const newLanguage = language === "ru" ? "en" : "ru";
        setLanguage(newLanguage);
        localStorage.setItem("language", newLanguage);
    };

    const onShowRolesModal = () => {
        openModal(<ModalContent close={close}
                                title={t("rolesModalTitle")}
                                showButtonGroup={false}>
                <RoleMatrix/>
            </ModalContent>
        );
    };

    const CdocLogo = () => (
        <div className={styles.logoContainer}>
            <RouterLink className={styles.logoWrapper} to={routes.root}>
                {theme === "light" ? <Logo/> : <LogoDark/>}
            </RouterLink>
            {!!userInfo && !userInfo?.isExternal && platform === Platform.DESKTOP && <TabMenu onChange={(tabId) => {
                const templatesPaths = ["my-templates", "documents"];
                const path = location.pathname.split("/");

                if (templatesPaths.includes(path[1]) && templatesPaths.includes(tabId)) {
                    const templateId = path[2];
                    if (templateId) {
                        navigate(`${tabId}/${templateId}`);
                    } else {
                        navigate(tabId);
                    }
                } else {
                    navigate(tabId);
                }
            }} activeTab={location.pathname.split("/")[1]} tabs={[
                // {
                //     id: '',
                //     disabled: true,
                //     content: t('main'),
                // },
                {
                    id: "documents",
                    content: t("documents")
                },
                {
                    id: "my-templates",
                    content: t("templates")
                },
            ]}/>}
        </div>
    );

    const actions: ReactNode[] = useMemo(() => {
        const acts: ReactNode[] = [
            <Button
                key="theme-toggler"
                view="ghost"
                title={theme === "light" ? t("darkTheme") : t("lightTheme")}
                icon={theme === "light" ? dayCheckedIcon : dayIcon}
                onClick={onToggleTheme}
            />,
            <Button
                key="language-toggler"
                view="ghost"
                title={t("changeLanguage")}
                icon={language === "ru" ? RuIcon : EnIcon}
                onClick={onLanguageChanged}
            />
        ];

        if (canSeeRoleMatrix && platform === Platform.DESKTOP) {
            acts.unshift(<Button
                key="roles-btn"
                view="ghost"
                onClick={() => onShowRolesModal()}
                title={t("roles")}
                icon={hrIcon}
            />);
        }
        if (!!userInfo && !userInfo.isExternal && platform === Platform.DESKTOP) {
            acts.unshift(<Button
                key="neuro-btn"
                view="neuro"
                onClick={() => navigate("/neurodoc")}
            >
                <div className={styles.buttonInnerContainer}>
                    <AiIcon size={16}/>
                    {t("createAITemplate")}
                </div>

            </Button>);
        }

        return acts;

    }, [theme, language, i18n.language, canSeeRoleMatrix, platform, navigate, userInfo]);

    const popupActions: ItemSimple[] = useMemo(() => {
        return [
            {
                value: "staff",
                content: (
                    <PopupAction
                        key="staff"
                        text={t("onStaff")}
                        icon={<Icon glyph="profile"/>}
                        url={`https://${hosts.staff.host}/${userInfo?.login}`}
                    />
                ) as ReactNode,
            }
        ];
    }, [i18n.language]);

    return !isIframe ? <HostsContextProvider hosts={hosts}>
        <HrHeader
            className={cn(className, styles.pageHeader, platform === Platform.DESKTOP ? styles.pageHeaderDesktop : "")}
            login={!!userInfo && !userInfo.isExternal ? userInfo?.login : undefined}
            showAvatarSkeleton={false}
            menuType={MenuType.TABS}
            desktopLogo={<CdocLogo/>}
            mobileLogo={<CdocLogo/>}
            platform={platform}
            actions={actions}
            popupActions={!!userInfo && !userInfo.isExternal ? popupActions : undefined}
        />
    </HostsContextProvider> : null;
});
